import { Card, CircularProgress, Grid, Icon } from "@mui/material";
import FormField from "components/FormField/FormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Formik, Form, FieldArray, FormikErrors } from "formik";
import * as Yup from "yup";
import { useGetAllContent, useUpdateContents } from "../content.hook";
import { Content, ETypeContent } from "../content.type";
import MDEditorShuttle from "components/MDEditor/MBEditorShuttle";

const scheduleIds = [
    ETypeContent.shuttle1,
    ETypeContent.shuttle2,
    ETypeContent.shuttle3,
    ETypeContent.shuttle4,
];

function ShuttleSchedule(): JSX.Element {
    const { data, isLoading } = useGetAllContent();
    const updateContents = useUpdateContents();

    const initialValues = scheduleIds.reduce((acc, id) => {
        const content = data?.find((c) => c.id === id) as Partial<Content> || {};
        acc[id] = {
            id,
            title_fr: content.title_fr || "",
            title_en: content.title_en || "",
            period_fr: content.period_fr || "",
            period_en: content.period_en || "",
            body_fr: content.body_fr || "",
            body_en: content.body_en || "",
            subContent: content.subContent || [],
        };

        return acc;
    }, {} as Record<string, Content>);

    const validationSchema = Yup.object().shape(
        scheduleIds.reduce((acc, id) => {
            acc[id] = Yup.object().shape({
                title_fr: Yup.string().required("champs obligatoire"),
                title_en: Yup.string().required("champs obligatoire"),
                period_fr: Yup.string().required("champs obligatoire"),
                period_en: Yup.string().required("champs obligatoire"),
                body_fr: Yup.string().required("champs obligatoire"),
                body_en: Yup.string().required("champs obligatoire"),
                subContent: Yup.array().of(
                    Yup.object().shape({
                        title_fr: Yup.string().required("champs obligatoire"),
                        title_en: Yup.string().required("champs obligatoire"),
                        body_fr: Yup.string().required("champs obligatoire"),
                    })
                ),
            });
            return acc;
        }, {} as Record<string, Yup.ObjectSchema<any>>)
    );

    const handleSubmit = async (values: typeof initialValues, { resetForm }: any) => {
        try {
            const contents: Content[] = Object.entries(values).map(([id, val]) => ({
                id,
                title_fr: val.title_fr,
                title_en: val.title_en,
                period_fr: val.period_fr,
                period_en: val.period_en,
                body_fr: val.body_fr,
                body_en: val.body_en,
                subContent: val.subContent.map((sub) => ({
                    ...sub,
                    content_id: id,
                    body_en: sub.body_fr,
                })),
            }));

            await updateContents.mutateAsync(contents);
            resetForm({ values });
        } catch (error) {
            console.error(error);
            alert("Erreur lors de la mise à jour");
        }
    };


    return isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
            <CircularProgress color="inherit" />
        </MDBox>
    ) : (
        <MDBox mt={4}>
            <Card style={{ overflowY: "auto" }}>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
                    {({ values, setFieldValue, dirty, isSubmitting, errors, touched }) => {
                        /*if (process.env.NODE_ENV === 'development' && Object.keys(errors).length) {
                            console.log('formik errors', errors);
                        }*/
                        return (
                            <Form autoComplete="off">
                                <MDBox p={4}>
                                    {scheduleIds.map((id, index) => (
                                        <MDBox key={id} mb={6} p={3} border="1px solid #eee" borderRadius={8}>
                                            <MDTypography variant="h5" fontWeight="medium" mb={2}>
                                                Horaire {index + 1}
                                            </MDTypography>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type="text"
                                                        label="Jours - FR"
                                                        name={`${id}.title_fr`}
                                                        value={values[id].title_fr}
                                                        onChange={(e: any) => setFieldValue(`${id}.title_fr`, e.target.value)}
                                                        error={Boolean(errors[id]?.title_fr && touched[id]?.title_fr)}
                                                        success={!errors[id]?.title_fr && touched[id]?.title_fr}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type="text"
                                                        label="Jours - EN"
                                                        name={`${id}.title_en`}
                                                        value={values[id].title_en}
                                                        onChange={(e: any) => setFieldValue(`${id}.title_en`, e.target.value)}
                                                        error={Boolean(errors[id]?.title_en && touched[id]?.title_en)}
                                                        success={!errors[id]?.title_en && touched[id]?.title_en}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type="text"
                                                        label="Période - FR"
                                                        name={`${id}.period_fr`}
                                                        value={values[id].period_fr}
                                                        onChange={(e: any) => setFieldValue(`${id}.period_fr`, e.target.value)}
                                                        error={Boolean(errors[id]?.period_fr && touched[id]?.period_fr)}
                                                        success={!errors[id]?.period_fr && touched[id]?.period_fr}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type="text"
                                                        label="Période - EN"
                                                        name={`${id}.period_en`}
                                                        value={values[id].period_en}
                                                        onChange={(e: any) => setFieldValue(`${id}.period_en`, e.target.value)}
                                                        error={Boolean(errors[id]?.period_en && touched[id]?.period_en)}
                                                        success={!errors[id]?.period_en && touched[id]?.period_en}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type="text"
                                                        label="Type d'horaire - FR"
                                                        name={`${id}.body_fr`}
                                                        value={values[id].body_fr}
                                                        onChange={(e: any) => setFieldValue(`${id}.body_fr`, e.target.value)}
                                                        error={Boolean(errors[id]?.body_fr && touched[id]?.body_fr)}
                                                        success={!errors[id]?.body_fr && touched[id]?.body_fr}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type="text"
                                                        label="Type d'horaire - EN"
                                                        name={`${id}.body_en`}
                                                        value={values[id].body_en}
                                                        onChange={(e: any) => setFieldValue(`${id}.body_en`, e.target.value)}
                                                        error={Boolean(errors[id]?.body_en && touched[id]?.body_en)}
                                                        success={!errors[id]?.body_en && touched[id]?.body_en}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <FieldArray
                                                name={`${id}.subContent`}
                                                render={(arrayHelpers) => (
                                                    <MDBox mt={4}>
                                                        {values[id].subContent.map((sub, idx) => (
                                                            <MDBox
                                                                key={idx}
                                                                sx={{ backgroundColor: "#EEEEEE", borderRadius: 2, p: 2, mb: 4 }}
                                                            >
                                                                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                                    <MDTypography variant="h5" fontWeight="medium">
                                                                        Circuit {idx + 1}
                                                                    </MDTypography>
                                                                    <MDButton
                                                                        variant="text"
                                                                        color="error"
                                                                        onClick={() => arrayHelpers.remove(idx)}
                                                                    >
                                                                        <Icon>delete</Icon>
                                                                    </MDButton>
                                                                </MDBox>

                                                                <MDBox mx={2} mt={2}>
                                                                    <Grid container spacing={3}>
                                                                        <Grid item xs={12} sm={6}>
                                                                            <FormField
                                                                                type="text"
                                                                                label="Titre - FR"
                                                                                name={`${id}.subContent.${idx}.title_fr`}
                                                                                value={sub.title_fr}
                                                                                onChange={(e: any) =>
                                                                                    setFieldValue(`${id}.subContent.${idx}.title_fr`, e.target.value)
                                                                                }
                                                                                error={Boolean((errors[id]?.subContent?.[idx] as FormikErrors<any>)?.title_fr && touched[id]?.subContent?.[idx]?.title_fr)}
                                                                                success={!((errors[id]?.subContent?.[idx] as FormikErrors<any>)?.title_fr) && touched[id]?.subContent?.[idx]?.title_fr}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={6}>
                                                                            <FormField
                                                                                type="text"
                                                                                label="Titre - EN"
                                                                                name={`${id}.subContent.${idx}.title_en`}
                                                                                value={sub.title_en}
                                                                                onChange={(e: any) =>
                                                                                    setFieldValue(`${id}.subContent.${idx}.title_en`, e.target.value)
                                                                                }
                                                                                error={Boolean((errors[id]?.subContent?.[idx] as FormikErrors<any>)?.title_en && touched[id]?.subContent?.[idx]?.title_en)}
                                                                                success={!((errors[id]?.subContent?.[idx] as FormikErrors<any>)?.title_en) && touched[id]?.subContent?.[idx]?.title_en}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12}>
                                                                            <MDBox mb={1} lineHeight={0} display="inline-block">
                                                                                <MDTypography
                                                                                    component="label"
                                                                                    variant="button"
                                                                                    fontWeight="regular"
                                                                                    color="text"
                                                                                >
                                                                                    Description *
                                                                                </MDTypography>
                                                                            </MDBox>
                                                                            <MDEditorShuttle
                                                                                value={sub.body_fr || ""}
                                                                                onChange={(content) =>
                                                                                    setFieldValue(`${id}.subContent.${idx}.body_fr`, content)
                                                                                }
                                                                                maxLength={500}
                                                                            />

                                                                        </Grid>
                                                                    </Grid>
                                                                </MDBox>
                                                            </MDBox>
                                                        ))}
                                                        <MDButton
                                                            type="button"
                                                            onClick={() => arrayHelpers.push({ title_fr: "", title_en: "", body_fr: "" })}
                                                            variant="outlined"
                                                            color="info"
                                                        >
                                                            + Ajouter un circuit
                                                        </MDButton>
                                                    </MDBox>
                                                )}
                                            />
                                        </MDBox>
                                    ))}
                                    <MDBox display="flex" justifyContent="flex-end">
                                        <MDButton
                                            disabled={!dirty || isSubmitting}
                                            type="submit"
                                            variant="gradient"
                                            color="info"
                                            sx={{ position: 'relative', minWidth: '100px' }}
                                        >
                                            Valider
                                            {isSubmitting && (
                                                <CircularProgress
                                                    size={24}
                                                    sx={{
                                                        color: 'inherit',
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        marginTop: '-12px',
                                                        marginLeft: '-12px',
                                                    }}
                                                />
                                            )}
                                        </MDButton>

                                    </MDBox>
                                </MDBox>
                            </Form>
                        );
                    }}
                </Formik>
            </Card>
        </MDBox>
    );
}

export default ShuttleSchedule;
